import { useEffect, useState } from "react";
import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage } from "@/App";
import { guideApi } from "@/lib/guide";
import { colorFor } from "@/lib/guideColors";
import * as Icons from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink, Bot, BookOpen, Eye, HelpCircle, Sparkles, Loader2, Link2 } from "lucide-react";
import BackBar from "@/components/BackBar";

export default function GuideArticle() {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    setLoading(true);
    guideApi.get(slug).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    guideApi.categories().then(setCategories).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return <DashboardLayout title=""><div className="space-y-3"><div className="glass-card h-32 skeleton rounded-2xl" /><div className="glass-card h-96 skeleton rounded-2xl" /></div></DashboardLayout>;
  }
  if (!data) {
    return <DashboardLayout title={t("غير موجود", "Not found")}><div className="glass-card rounded-2xl p-10 text-center text-slate-400">{t("المقال غير متاح", "Article unavailable")}</div></DashboardLayout>;
  }

  const askAssistant = () => {
    navigate(`/ai-assistant?prefill=${encodeURIComponent(`بناءً على مقال "${data.title}" في دليل عرب سند: `)}&context=${encodeURIComponent(`/guide/article/${data.slug}`)}`);
  };

  const cat = categories.find((c) => c.key === data.category);
  const cc = colorFor(cat?.color);
  const catIcon = (cat && Icons[cat.icon]) || BookOpen;

  // Render content as paragraphs/headers (simple markdown-ish)
  const renderContent = (txt) => {
    return txt.split(/\n\n+/).map((block, i) => {
      const trimmed = block.trim();
      if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
        return <h3 key={i} className="text-lg font-bold text-yellow-400 mt-5">{trimmed.replace(/\*\*/g, "")}</h3>;
      }
      // Inline bold
      const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-slate-300 leading-relaxed whitespace-pre-wrap">
          {parts.map((p, j) => p.startsWith("**") && p.endsWith("**")
            ? <strong key={j} className="text-white font-semibold">{p.replace(/\*\*/g, "")}</strong>
            : <span key={j}>{p}</span>)}
        </p>
      );
    });
  };

  return (
    <DashboardLayout title={data.title}>
      <div className="max-w-4xl space-y-6" data-testid="guide-article">
        <BackBar to="/guide" label_ar="دليل المهاجر" label_en="Immigrant Guide"
          crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { to: "/guide", ar: "دليل المهاجر", en: "Guide" }, { ar: data.title, en: data.title }]} />

        <div className={`glass-card rounded-3xl p-6 lg:p-8 relative overflow-hidden border ${cc.border}`}>
          <div className={`absolute -top-32 -right-32 w-96 h-96 ${cc.glow} blur-[120px] rounded-full`} />
          <div className="relative space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${cc.bg} ${cc.text}`}>
                {React.createElement(catIcon, { size: 12, className: "ml-1" })}
                {cat ? (language === "ar" ? cat.ar : cat.en) : data.category}
              </Badge>
              {data.featured && <Badge className="bg-purple-500/20 text-purple-300"><Sparkles size={12} className="ml-1" />{t("مميز", "Featured")}</Badge>}
              <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Eye size={12} />{data.views || 0}</span>
            </div>
            <h1 className="text-2xl lg:text-4xl font-bold text-white">{data.title}</h1>
            {data.title_en && <p className="text-sm text-slate-500 italic">{data.title_en}</p>}
            <p className="text-base lg:text-lg text-slate-300 leading-relaxed">{data.summary}</p>
            <Button onClick={askAssistant} className="btn-primary mt-2" data-testid="ask-ai-btn">
              <Bot size={18} className="ml-2" />{t("اسأل مساعد عرب سند عن هذا الموضوع", "Ask Arab Sanad Assistant about this")}
            </Button>
          </div>
        </div>

        {/* Content */}
        <article className="glass-card rounded-2xl p-6 lg:p-8 space-y-3 prose-arab">
          {renderContent(data.content)}
        </article>

        {/* FAQ */}
        {data.faq?.length > 0 && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <HelpCircle className="text-yellow-500" size={20} />
              {t("الأسئلة الشائعة", "FAQ")}
            </h3>
            <div className="space-y-4">
              {data.faq.map((f, i) => (
                <div key={i} className="border-b border-slate-800 pb-3 last:border-0" data-testid={`faq-${i}`}>
                  <p className="font-semibold text-yellow-400 mb-1">س: {f.q}</p>
                  <p className="text-slate-300 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Official Resources */}
        {data.official_resources?.length > 0 && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Link2 className="text-yellow-500" size={18} />
              {t("المصادر الرسمية", "Official Resources")}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.official_resources.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                   className="flex items-center justify-between bg-slate-900 border border-slate-700 hover:border-yellow-500 rounded-xl px-4 py-3 transition" data-testid={`resource-${i}`}>
                  <span className="text-white text-sm">{r.label}</span>
                  <ExternalLink size={14} className="text-yellow-500" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Related services */}
        {data.related_services?.length > 0 && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-3">{t("خدمات عرب سند ذات صلة", "Related Arab Sanad Services")}</h3>
            <div className="flex flex-wrap gap-2">
              {data.related_services.map((s, i) => (
                <Link key={i} to={s} className="px-4 py-2 bg-yellow-500/15 text-yellow-400 rounded-xl text-sm hover:bg-yellow-500 hover:text-black transition" data-testid={`svc-link-${i}`}>
                  {s}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related articles */}
        {data.related?.length > 0 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-4">{t("مقالات ذات صلة", "Related Articles")}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.related.map((r) => {
                const rcat = categories.find((c) => c.key === r.category);
                const rcc = colorFor(rcat?.color);
                return (
                <Link key={r.slug} to={`/guide/article/${r.slug}`} className="glass-card rounded-2xl p-4 hover:scale-[1.02] transition-all" data-testid={`related-${r.slug}`}>
                  <Badge className={`bg-slate-800 text-[10px] mb-2 ${rcc.text}`}>{rcat ? (language === "ar" ? rcat.ar : rcat.en) : r.category}</Badge>
                  <h4 className="font-semibold text-white mb-1 line-clamp-2">{r.title}</h4>
                  <p className="text-xs text-slate-400 line-clamp-2">{r.summary}</p>
                </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
